import * as RadixDialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { useTranslation } from '@lichtblick/i18next';
import { Close, Delete } from '@lichtblick/icons';
import { Colors, TextVariants } from '@lichtblick/theme';

import {
  ActionsContainer,
  BusyOverlay,
  BusyOverlayContent,
  Header,
  ContentContainer,
  NegativeActionsContainer,
  DeleteDialog,
  Container,
  ScrollArea,
  DrawerCloseButton,
} from './Drawer.styles';

import { Button } from '../../Button';
import { IconButton } from '../../IconButton';
import { PacmanLoaderComponent } from '../../Skeleton';
import { Text } from '../../Text';
import { DrawerPanel } from '../DrawerPanel';

type MessageDialogProps = {
  acceptLabel?: string;
  cancelLabel?: string;
  children?: React.ReactElement;
  deleteText?: string;
  description?: string;
  isActionContainer?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  manualAcceptLabel?: string;
  onAccept?: () => void;
  onClose?: () => void;
  onDelete?: () => void;
  showLoadingOverlay?: boolean;
  title?: string;
  trigger?: React.ReactElement;
  widthOverride?: string;
};

export const Drawer = ({
  acceptLabel,
  cancelLabel,
  children,
  deleteText,
  description,
  isActionContainer = true,
  isLoading = false,
  isOpen,
  manualAcceptLabel,
  onAccept,
  onClose,
  onDelete,
  showLoadingOverlay = false,
  title,
  trigger,
  widthOverride,
}: MessageDialogProps) => {
  const isCloseDisabled = showLoadingOverlay;
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState<boolean>(false);
  const { t } = useTranslation();

  return (
    <DrawerPanel
      accessibilityTitle={title ?? 'Drawer'}
      isCloseDisabled={isCloseDisabled}
      isOpen={isOpen ?? false}
      onClose={() => onClose?.()}
      trigger={trigger}
      widthOverride={widthOverride}
    >
      {title && (
        <Header>
          {isLoading ? <Skeleton width="14rem" /> : <Text $variant={TextVariants.header.m}>{title}</Text>}
          {description && (
            <Text $variant={TextVariants.body.l} color={Colors.gray[100]}>
              {isLoading ? <Skeleton width="24rem" /> : description}
            </Text>
          )}
        </Header>
      )}
      <ContentContainer>
        {isLoading ? (
          <Container>
            <Skeleton count={1.5} />
            <Skeleton count={1.3} />
            <Skeleton count={1.7} />
          </Container>
        ) : (
          <>
            <ScrollArea>{children}</ScrollArea>
            {isActionContainer && (
              <ActionsContainer>
                <NegativeActionsContainer>
                  {onDelete && (
                    <DeleteDialog
                      acceptLabel={t('delete')}
                      cancelLabel={t('cancel')}
                      description={deleteText ?? ''}
                      isOpen={isDeleteConfirmOpen}
                      onAccept={() => {
                        onDelete();
                        setIsDeleteConfirmOpen(false);
                      }}
                      onCancel={() => setIsDeleteConfirmOpen(false)}
                      onClose={() => setIsDeleteConfirmOpen(false)}
                      trigger={
                        <IconButton
                          icon={<Delete />}
                          isDisabled={isCloseDisabled}
                          onClick={() => setIsDeleteConfirmOpen(true)}
                          variant="rejection"
                        />
                      }
                    />
                  )}
                  {cancelLabel && (
                    <RadixDialog.Close asChild>
                      <Button
                        isDisabled={isCloseDisabled}
                        label={cancelLabel}
                        onClick={() => onClose?.()}
                        variant="secondary"
                      />
                    </RadixDialog.Close>
                  )}
                </NegativeActionsContainer>
                {acceptLabel && (
                  <RadixDialog.Close asChild>
                    <Button isDisabled={isCloseDisabled} label={acceptLabel} onClick={onAccept} />
                  </RadixDialog.Close>
                )}
                {manualAcceptLabel && (
                  <Button isDisabled={isCloseDisabled} label={manualAcceptLabel} onClick={onAccept} />
                )}
              </ActionsContainer>
            )}
          </>
        )}
      </ContentContainer>
      <DrawerCloseButton>
        <RadixDialog.Close asChild>
          <IconButton icon={<Close />} isDisabled={isCloseDisabled} onClick={() => onClose?.()} variant="transparent" />
        </RadixDialog.Close>
      </DrawerCloseButton>

      {showLoadingOverlay && (
        <BusyOverlay>
          <BusyOverlayContent>
            <PacmanLoaderComponent />
          </BusyOverlayContent>
        </BusyOverlay>
      )}
    </DrawerPanel>
  );
};
